import { useState } from 'react';

// Structure des paramètres typée pour correspondre au payload attendu par ton API
export interface AccountSettingsPayload {
  notifications: {
    pushNotifications: boolean;
    emailNotifications: boolean;
    smsNotifications: boolean;
    orderUpdates: boolean;
    promotions: boolean;
    weeklyReport: boolean;
  };
  language: string;
  privacy: {
    shareLocation: boolean;
    shareProfile: boolean;
    allowTracking: boolean;
  };
  delivery: {
    preferredTimeSlot: string;
    specialInstructions: string;
    allowContactless: boolean;
  };
}

interface AccountSettingsProps {
  onSave: (settings: AccountSettingsPayload) => void;
}

export default function AccountSettings({ onSave }: AccountSettingsProps) {
  const [activeSection, setActiveSection] = useState('notifications');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [settings, setSettings] = useState<AccountSettingsPayload>({
    notifications: {
      pushNotifications: true,
      emailNotifications: true,
      smsNotifications: true, // Activé par défaut (crucial pour le suivi en Afrique)
      orderUpdates: true,
      promotions: false,
      weeklyReport: true
    },
    language: 'fr',
    privacy: {
      shareLocation: true,
      shareProfile: false,
      allowTracking: true
    },
    delivery: {
      preferredTimeSlot: 'morning',
      specialInstructions: '',
      allowContactless: true
    }
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const timeSlots = [
    { id: 'morning', label: 'Matinée (08h - 12h)', icon: 'ri-sun-line' },
    { id: 'afternoon', label: 'Après-midi (12h - 17h)', icon: 'ri-sun-fill' },
    { id: 'evening', label: 'Fin de journée (17h - 20h)', icon: 'ri-moon-line' },
    { id: 'flexible', label: 'Créneau flexible', icon: 'ri-time-line' }
  ];

  const triggerToast = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleNotificationChange = (key: keyof AccountSettingsPayload['notifications'], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const handlePrivacyChange = (key: keyof AccountSettingsPayload['privacy'], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      privacy: { ...prev.privacy, [key]: value }
    }));
  };

  const handleDeliveryChange = (key: keyof AccountSettingsPayload['delivery'], value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      delivery: { ...prev.delivery, [key]: value }
    }));
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    triggerToast('Votre mot de passe a été modifié avec succès.');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordForm(false);
  };

  const handleSaveSettings = () => {
    onSave(settings);
    triggerToast('Vos configurations ont été synchronisées.');
  };

  const sections = [
    { id: 'notifications', label: 'Alertes & SMS', icon: 'ri-notification-3-line' },
    { id: 'language', label: 'Langue', icon: 'ri-global-line' },
    { id: 'privacy', label: 'Confidentialité', icon: 'ri-shield-user-line' },
    { id: 'delivery', label: 'Préférences de livraison', icon: 'ri-truck-line' },
    { id: 'security', label: 'Sécurité', icon: 'ri-lock-line' }
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">

      {/* Toast Notification Intégré */}
      {successMessage && (
        <div className="fixed bottom-20 md:bottom-6 right-4 z-50 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-lg border border-slate-800 flex items-center space-x-3 text-xs md:text-sm animate-fade-in-up">
          <i className="ri-checkbox-circle-fill text-teal-400 text-lg"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Navigation Horizontale Tactile / Desktop List */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 md:p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Paramètres</h3>
            <p className="text-xs text-slate-400 hidden sm:block">Ajustez vos préférences d'application et de livraison</p>
          </div>
        </div>

        {/* Barre défilante sur mobile pour une navigation fluide au doigt */}
        <div className="flex overflow-x-auto gap-1.5 pb-2 pt-1 no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
          {sections.map((section) => {
            const isSelected = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap border ${isSelected
                    ? 'bg-slate-900 border-slate-900 text-white font-semibold shadow-sm'
                    : 'bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                  }`}
              >
                <i className={`${section.icon} text-sm`}></i>
                <span>{section.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── CONTENU DES SECTIONS ───────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 md:p-6 shadow-sm min-h-[280px]">

        {/* 1. SECTION NOTIFICATIONS */}
        {activeSection === 'notifications' && (
          <div className="space-y-5">
            <div>
              <h4 className="text-base font-bold text-slate-800">Canaux de communication</h4>
              <p className="text-xs text-slate-400 mt-0.5">Choisissez comment vous souhaitez être informé de l'état de vos colis.</p>
            </div>

            <div className="divide-y divide-slate-100">
              {[
                { id: 'smsNotifications', title: 'Suivi par SMS', desc: 'Indispensable pour recevoir le code OTP du livreur sans connexion internet.' },
                { id: 'pushNotifications', title: 'Notifications de l\'application', desc: 'Alertes instantanées sur l\'état du transit et alertes de proximité.' },
                { id: 'emailNotifications', title: 'Factures & Bilans par Email', desc: 'Réception systématique de vos reçus de douane et bordereaux d\'envoi.' },
                { id: 'orderUpdates', title: 'Changements d\'étapes de livraison', desc: 'Notifications automatiques à chaque prise en charge.' },
                { id: 'promotions', title: 'Offres promotionnelles', desc: 'Codes de réduction sur les trajets internationaux ou urbains.' },
                { id: 'weeklyReport', title: 'Synthèse d\'activité', desc: 'Rapport hebdomadaire de vos volumes d\'expéditions.' },
              ].map((item) => (
                <div key={item.id} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                  <div className="pr-4">
                    <h5 className="text-sm font-semibold text-slate-800">{item.title}</h5>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={settings.notifications[item.id as keyof AccountSettingsPayload['notifications']]}
                      onChange={(e) => handleNotificationChange(item.id as keyof AccountSettingsPayload['notifications'], e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 2. SECTION LANGUE */}
        {activeSection === 'language' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-base font-bold text-slate-800">Langue du système</h4>
              <p className="text-xs text-slate-400 mt-0.5">Sélectionnez votre langue d'usage pour l'ensemble de la plateforme.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {languages.map((lang) => {
                const isSelected = settings.language === lang.code;
                return (
                  <label
                    key={lang.code}
                    className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${isSelected ? 'border-slate-900 bg-slate-900/5 shadow-sm' : 'border-slate-100 hover:bg-slate-50'
                      }`}
                  >
                    <input
                      type="radio"
                      name="language"
                      value={lang.code}
                      checked={isSelected}
                      onChange={(e) => setSettings(prev => ({ ...prev, language: e.target.value }))}
                      className="w-4 h-4 text-slate-900 focus:ring-slate-900 border-slate-300"
                    />
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-sm font-semibold text-slate-800">{lang.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. SECTION CONFIDENTIALITÉ */}
        {activeSection === 'privacy' && (
          <div className="space-y-5">
            <div>
              <h4 className="text-base font-bold text-slate-800">Données privées & Traceurs</h4>
              <p className="text-xs text-slate-400 mt-0.5">Gérez la manière dont vos coordonnées et positions géographiques sont traitées.</p>
            </div>

            <div className="divide-y divide-slate-100">
              {[
                { id: 'shareLocation', title: 'Partage de localisation en arrière-plan', desc: 'Permet de pré-remplir instantanément l\'adresse de ramassage lors d\'une commande.' },
                { id: 'shareProfile', title: 'Visibilité du profil entreprise', desc: 'Rendre votre nom d\'entité repérable par les expéditeurs partenaires du réseau.' },
                { id: 'allowTracking', title: 'Suivi analytique anonymisé', desc: 'Aidez nos équipes techniques à optimiser les temps de chargement des cartes de suivi.' },
              ].map((item) => (
                <div key={item.id} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                  <div className="pr-4">
                    <h5 className="text-sm font-semibold text-slate-800">{item.title}</h5>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={settings.privacy[item.id as keyof AccountSettingsPayload['privacy']]}
                      onChange={(e) => handlePrivacyChange(item.id as keyof AccountSettingsPayload['privacy'], e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. SECTION PRÉFÉRENCES DE LIVRAISON */}
        {activeSection === 'delivery' && (
          <div className="space-y-5">
            <div>
              <h4 className="text-base font-bold text-slate-800">Préférences par défaut</h4>
              <p className="text-xs text-slate-400 mt-0.5">Gagnez du temps lors de vos prochaines demandes de livraison.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Créneau de livraison préférentiel
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {timeSlots.map((slot) => {
                    const isChecked = settings.delivery.preferredTimeSlot === slot.id;
                    return (
                      <label
                        key={slot.id}
                        className={`flex items-center space-x-3 p-3 border rounded-xl cursor-pointer transition-all ${isChecked ? 'border-slate-900 bg-slate-50' : 'border-slate-100 hover:bg-slate-50/50'
                          }`}
                      >
                        <input
                          type="radio"
                          name="timeSlot"
                          value={slot.id}
                          checked={isChecked}
                          onChange={(e) => handleDeliveryChange('preferredTimeSlot', e.target.value)}
                          className="w-4 h-4 text-slate-900 focus:ring-slate-900 border-slate-300"
                        />
                        <i className={`${slot.icon} text-slate-500`}></i>
                        <span className="text-xs font-semibold text-slate-700">{slot.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Notes permanentes pour les livreurs
                </label>
                <textarea
                  value={settings.delivery.specialInstructions}
                  onChange={(e) => handleDeliveryChange('specialInstructions', e.target.value)}
                  placeholder="Ex: 'Laisser au secrétariat si absent', 'Point de repère : à côté de la station service'..."
                  className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors placeholder:text-slate-300 resize-none"
                  rows={3}
                  maxLength={200}
                />
                <div className="text-[10px] text-slate-400 text-right mt-1">
                  {settings.delivery.specialInstructions.length}/200 caractères
                </div>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl">
                <div>
                  <h5 className="text-xs font-bold text-slate-800">Autoriser le dépôt sans contact</h5>
                  <p className="text-[11px] text-slate-400 mt-0.5">Le livreur peut déposer le colis auprès d'un tiers de confiance ou gardien.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                  <input
                    type="checkbox"
                    checked={settings.delivery.allowContactless}
                    onChange={(e) => handleDeliveryChange('allowContactless', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* 5. SECTION SÉCURITÉ */}
        {activeSection === 'security' && (
          <div className="space-y-5">
            <div>
              <h4 className="text-base font-bold text-slate-800">Authentification & Accès</h4>
              <p className="text-xs text-slate-400 mt-0.5">Protégez l'accès à vos informations de facturation et d'historique.</p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-xl gap-3">
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-check-line text-emerald-600 text-xl mt-0.5"></i>
                  <div>
                    <h5 className="text-sm font-semibold text-slate-800">Protection du compte active</h5>
                    <p className="text-xs text-slate-400 mt-0.5">Votre mot de passe a été mis à jour il y a 2 mois.</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPasswordError(null);
                    setShowPasswordForm(!showPasswordForm);
                  }}
                  className="self-start sm:self-auto px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold transition-all"
                >
                  {showPasswordForm ? 'Masquer le formulaire' : 'Modifier le mot de passe'}
                </button>
              </div>

              {showPasswordForm && (
                <form onSubmit={handlePasswordChange} className="space-y-3.5 p-4 border border-slate-100 rounded-xl bg-white animate-fade-in">
                  {passwordError && (
                    <div className="p-3 bg-rose-50 text-rose-600 text-xs font-medium rounded-xl border border-rose-100">
                      {passwordError}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                      Mot de passe actuel
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Nouveau mot de passe
                      </label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full text-sm px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors"
                        minLength={8}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Confirmer le mot de passe
                      </label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full text-sm px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors"
                        minLength={8}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex space-x-2 pt-1">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-bold transition-all"
                    >
                      Confirmer le changement
                    </button>
                  </div>
                </form>
              )}

              <div className="flex items-center justify-between py-3 border-b border-slate-50">
                <div>
                  <h5 className="text-sm font-semibold text-slate-800">Double authentification (2FA)</h5>
                  <p className="text-xs text-slate-400 mt-0.5">Sécurisez vos transactions par code OTP SMS.</p>
                </div>
                <button className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-semibold transition-all">
                  Activer
                </button>
              </div>

              <div className="flex items-center justify-between py-3 last:border-0">
                <div>
                  <h5 className="text-sm font-semibold text-slate-800">Sessions de connexion actives</h5>
                  <p className="text-xs text-slate-400 mt-0.5">Surveillez les terminaux connectés à votre compte.</p>
                </div>
                <button className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-semibold transition-all">
                  Inspecter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Barre d'action finale globale (Fixe ou intégrée au flux) */}
      <div className="bg-slate-900 rounded-2xl p-4 md:p-5 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-md">
        <div>
          <h4 className="text-sm font-bold tracking-tight">Modifications en attente</h4>
          <p className="text-xs text-slate-400 mt-0.5">Pensez à enregistrer pour appliquer vos nouveaux choix logistiques.</p>
        </div>
        <button
          onClick={handleSaveSettings}
          className="w-full sm:w-auto px-5 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-xl text-xs transition-all shadow-sm flex items-center justify-center space-x-2"
        >
          <i className="ri-save-line text-sm"></i>
          <span>Enregistrer les préférences</span>
        </button>
      </div>
    </div>
  );
}